import React from "react";
import T from "prop-types";
import { Map } from "immutable";
import { Field } from "redux-form";
import {
  dataManager,
  manageFields,
  onChange,
  translateProps,
  withFormFields
} from "MODULES/shared/decorators";
import * as F from "MODULES/shared/components/Forms/FormTypes";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import { AS_IS, QUALITY_CONTROL } from "MODULES/users/constants";
import recordCollector from "LIB/recordCollector";
import api from "APP_ROOT/api";
import { Col } from "react-bootstrap";
import { properCase } from "LIB/formatters/text";
import { WARRANTY } from "MODULES/OutgoingQuotes/constants";
import { arrayToOptions } from "LIB/forms";
import { WARRANTY_OPTIONS } from "MODULES/inventories/constants";
import {
  FormRow,
  Select,
  Check,
  CheckGroup
} from "MODULES/shared/components/Forms";
import { PART } from "MODULES/parts/constants";
import { hasRole } from "MODULES/users/helpers";
import PartNumber from "./PartNumber";
import SellAsAlt from "./SellAsAlt";
import Inventory from "./Inventory";
import Flags from "./Flags";
import PriceDetails from "./PriceDetails";

const separator = <hr className="hr-xs hr-dashed" />;

export function PartDetails({
  associations,
  cancelled,
  canEditPartDetails,
  canEditQcPreapproval,
  canEditHazmatFlags,
  change,
  condition,
  showTable,
  dim_weight: dimWeight,
  disableQcPreapproval,
  exchanged,
  exchange_sale: exchangeSale,
  hazmatFee,
  inventoryId,
  lineItem,
  onInventorySelected,
  part,
  paid,
  commissionPaid,
  shipped,
  updatePart,
  currentUser
}) {
  const { alternates = [], inventories = [] } = associations;
  const partAndInventoryLocked = !lineItem.isEmpty();

  return (
    <div style={{ marginTop: "26px" }}>
      <FormRow>
        <Col md={6}>
          <PartNumber
            part={part}
            onSelect={updatePart}
            note="To add a Part #, search for a part that is reserved for sale"
            disabled={
              cancelled ||
              partAndInventoryLocked ||
              exchanged ||
              shipped ||
              paid
            }
          />
        </Col>
        <Col md={6}>
          <SellAsAlt
            alternates={alternates}
            cancelled={cancelled}
            exchanged={exchanged}
            shipped={shipped || paid}
            canEditPartDetails={canEditPartDetails}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col md={12}>
          <Inventory
            condition={condition}
            showTable={showTable}
            dim_weight={dimWeight}
            disabled={
              cancelled ||
              partAndInventoryLocked ||
              exchanged ||
              shipped ||
              paid ||
              !canEditPartDetails
            }
            inventory_id={inventoryId}
            inventories={inventories}
            onSelect={onInventorySelected}
          />
        </Col>
      </FormRow>
      {separator}
      <PriceDetails
        disabledPrice={cancelled || exchanged || paid}
        disabledCondition={cancelled || shipped || exchanged || paid}
        exchangeSale={exchangeSale}
        commissionPaid={commissionPaid}
      />
      <FormRow>
        <Col md={6}>
          <Field
            component={Select}
            label={properCase(WARRANTY)}
            name={WARRANTY}
            options={arrayToOptions(WARRANTY_OPTIONS)}
            disabled={cancelled || exchanged || paid}
          />
        </Col>
        <Col md={6}>
          <CheckGroup title="Sold As Is" justified>
            <Field
              name={AS_IS}
              component={Check}
              label={"As-Is / No Returns"}
            />
          </CheckGroup>
        </Col>
      </FormRow>
      {separator}
      <Flags
        disabled={
          cancelled || exchanged || shipped || paid || !canEditPartDetails
        }
        hazmatFee={hazmatFee}
        change={change}
        canEditQcPreapproval={canEditQcPreapproval}
        canEditHazmatFlags={canEditHazmatFlags}
        canEditExportComplianceHold={hasRole(currentUser, QUALITY_CONTROL)}
        disableQcPreapproval={disableQcPreapproval}
        lineItem={lineItem.toJS()}
      />
    </div>
  );
}

PartDetails.propTypes = {
  inventoryId: F.formField(F.malleable).isRequired,
  condition: F.formField(F.stringInput).isRequired,
  exchange_sale: F.formField(F.boolInput).isRequired,
  dim_weight: F.formField(F.boolInput).isRequired,
  lineItem: T.instanceOf(Map),
  part: T.shape({
    id: T.number.isRequired
  }),
  onInventorySelected: T.func.isRequired,
  updatePart: T.func.isRequired,
  canEditPartDetails: T.bool,
  canEditQcPreapproval: T.bool,
  canEditHazmatFlags: T.bool,
  disableQcPreapproval: T.bool,
  associations: T.shape({
    alternates: T.arrayOf(T.shape({})),
    inventories: T.arrayOf(T.shape({}))
  }).isRequired,
  cancelled: T.bool,
  paid: T.bool,
  commissionPaid: T.bool,
  shipped: T.bool,
  exchanged: T.bool,
  showTable: T.bool,
  ...WithCurrentUser
};

const loader = dataManager("associations", ({ part, currentUser }) => {
  if (part) {
    const alternates = recordCollector((page, limit) =>
      api.parts.alternates.search(part.id, { page, limit })
    );
    const fetchReservedInventories = (page, limit) =>
      api.inventories.searchReservedForSale({
        part_id: part.id,
        reserve_for_seller_id: currentUser.id,
        page,
        limit
      });

    const inventories = recordCollector(fetchReservedInventories);

    return Promise.all([alternates, inventories]).then(([alt, inv]) => ({
      alternates: alt,
      inventories: inv
    }));
  }

  return Promise.resolve();
});

const partManager = manageFields({});

const listener = onChange((prevProps, nextProps) => {
  if (
    nextProps.part &&
    (!prevProps.part || nextProps.part.id !== prevProps.part.id)
  ) {
    nextProps.reload();
  }
});

const withProps = translateProps(
  ({
    updateField,
    resetSpecialContainerFeeField,
    setPart,
    associations,
    inventory_id
  }) => ({
    updatePart: part => {
      updateField(PART)(part);
      if (resetSpecialContainerFeeField)
        resetSpecialContainerFeeField(part.special_container_fee);
      setPart(part);
    },
    associations: associations || {},
    inventoryId: inventory_id
  })
);

export default withFormFields(
  withCurrentUser(partManager(loader(listener(withProps(PartDetails))))),
  ["inventory_id", "condition", "dim_weight", "exchange_sale"]
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/Form/PartDetails/PartDetails.jsx